import React, { memo, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Dimensions,
  TouchableOpacity,
  Image,
} from "react-native";
import RBSheet from "react-native-raw-bottom-sheet";
import { createMaterialTopTabNavigator } from "@react-navigation/material-top-tabs";
import Cross from "../../../assets/svg/Cross";
import color from "../../../constants/color";
import { FlatList } from "react-native-gesture-handler";
import { TabData, ProductOrder, BottomSheetData, ReturnReason } from "../types";
import t from "@constants/localization";
import Error from "@assets/svg/Error";
import { PRODUCT_CARD_HEIGHT } from "../constants";

const PATH = "screens/Returns";

const Tab = createMaterialTopTabNavigator();

const ProductsList = ({ orders_list }: { orders_list: ProductOrder[] }) => {
  return (
    <FlatList
      data={orders_list}
      keyExtractor={(item) => item.order_id}
      renderItem={({ item }) => {
        return (
          <View key={item.order_id} style={styles.cardContainer}>
            <View style={styles.cardHeader}>
              <Text style={styles.cardHeaderTitle}>{item.user_name}</Text>
              <Text>
                {t(`${PATH}/orderId`)}
                <Text style={styles.cardHeaderOrderID}>{item.order_id}</Text>
              </Text>
            </View>
            <View style={styles.cardDetails}>
              <Image
                source={{ uri: item.product_image }}
                style={styles.cardImage}
              />
              <View style={styles.cardContent}>
                <View style={styles.cardContentText}>
                  <Text
                    style={styles.cardTitle}
                    ellipsizeMode="tail"
                    numberOfLines={2}
                  >
                    {item.short_name}
                  </Text>
                  <Text style={styles.cardQuantity}>
                    {t(`${PATH}/qty`, { qty: item.quantity })}
                  </Text>
                </View>
                <View>
                  <View style={styles.cardRow}>
                    {item.payment_status === "PREPAID" ? (
                      <Text style={styles.prepaidText}>Prepaid</Text>
                    ) : null}
                    <Text style={styles.refundAmount}>
                      ₹{item.refundAmount}
                    </Text>
                  </View>
                  <Text style={styles.refundText}>{t(`${PATH}/refund`)}</Text>
                </View>
              </View>
            </View>
          </View>
        );
      }}
      contentContainerStyle={styles.listContent}
    />
  );
};

const BottomSheet = ({
  bottomSheetRef,
  bottomSheetData,
  closeSheet,
}: {
  bottomSheetRef: React.MutableRefObject<RBSheet | undefined>;
  bottomSheetData: BottomSheetData;
  closeSheet: () => void | undefined;
}) => {
  const TabButton = memo(
    ({ focused, tab }: { focused: boolean; tab: TabData }) => (
      <Pressable
        android_ripple={{ color: color.shadow, borderless: true }}
        key={tab.title}
      >
        <View
          style={[
            styles.tabButton,
            focused ? styles.tabButtonFocused : styles.tabButtonUnfocused,
          ]}
        >
          <Text
            style={[
              styles.tabButtonText,
              focused
                ? styles.tabButtonTextFocused
                : styles.tabButtonTextUnfocused,
            ]}
          >
            {tab.title} ({tab?.data?.quantity})
          </Text>
        </View>
      </Pressable>
    ),
  );

  const renderTab = useCallback(
    (tab: TabData) => {
      return (
        <>
          {bottomSheetData.sectionKey === "customerRTO" && tab.name === "cm" ? (
            <View style={styles.warningContainer}>
              <View style={styles.warningIconContainer}>
                <Error height={10} width={10} />
              </View>
              <Text style={styles.warningText}>{t(`${PATH}/warning`)}</Text>
            </View>
          ) : null}
          <ProductsList orders_list={tab.data.orders_list} />
        </>
      );
    },
    [bottomSheetData],
  );

  return (
    <RBSheet
      ref={bottomSheetRef}
      useNativeDriver={true}
      customStyles={{
        container: [
          styles.sheetContainer,
          {
            height: bottomSheetData.height,
          },
        ],
      }}
      customModalProps={{
        animationType: "slide",
        statusBarTranslucent: true,
      }}
      customAvoidingViewProps={{
        enabled: false,
      }}
    >
      <TouchableOpacity
        activeOpacity={0.5}
        style={styles.closeIcon}
        onPress={closeSheet}
      >
        <Cross />
      </TouchableOpacity>
      <Text style={styles.sheetTitle}>
        {t(`${PATH}/${bottomSheetData.sectionKey as ReturnReason}`)}
      </Text>
      <Tab.Navigator
        backBehavior="none"
        screenOptions={() => ({
          tabBarIndicatorStyle: { height: 0 },
          lazy: true,
          tabBarStyle: {
            elevation: 0,
            height: bottomSheetData.tabs.length === 2 ? "auto" : 0,
          },
          tabBarPressColor: color.shadowLighter,
        })}
        sceneContainerStyle={styles.sceneContainer}
      >
        {bottomSheetData.tabs.map((tab: TabData) => (
          <Tab.Screen
            key={tab.name}
            options={{
              tabBarLabel: ({ focused }) => (
                <TabButton focused={focused} tab={tab} />
              ),
            }}
            name={tab.name}
            children={() => renderTab(tab)}
          />
        ))}
      </Tab.Navigator>
    </RBSheet>
  );
};

const styles = StyleSheet.create({
  sheetContainer: {
    paddingVertical: 16,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: "70%",
  },
  closeIcon: {
    backgroundColor: "#F4F4F4",
    alignSelf: "flex-end",
    borderRadius: 16,
    marginHorizontal: 16,
    height: 25,
    width: 25,
    alignItems: "center",
    justifyContent: "center",
  },
  sheetTitle: {
    fontWeight: "500",
    fontSize: 16,
    textAlign: "center",
    paddingBottom: 16,
  },
  warningContainer: {
    flexDirection: "row",
    marginHorizontal: 16,
  },
  warningIconContainer: {
    padding: 4,
  },
  warningText: {
    fontSize: 12,
    fontWeight: "400",
    color: color.errorRed,
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  cardContainer: {
    borderWidth: 1,
    borderRadius: 8,
    borderColor: color.lightGrey,
    marginBottom: 8,
    height: PRODUCT_CARD_HEIGHT,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: color.lightGrey,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  cardHeaderTitle: {
    fontSize: 13,
    fontWeight: "500",
  },
  cardHeaderOrderID: {
    fontSize: 13,
    fontWeight: "500",
  },
  cardDetails: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
  },
  cardImage: {
    width: 50,
    height: 50,
    marginRight: 16,
  },
  cardContent: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardContentText: {
    justifyContent: "space-between",
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: "500",
    maxWidth: 150,
  },
  cardQuantity: {
    fontSize: 12,
    color: color.grey,
    fontWeight: "500",
  },
  cardRow: {
    flexDirection: "row",
  },
  refundAmount: {
    fontSize: 14,
    color: color.black,
    fontWeight: "600",
    textAlign: "center",
  },
  refundText: {
    fontSize: 10,
    color: color.darkGrey,
    marginBottom: 4,
    fontWeight: "400",
    textAlign: "center",
  },
  tabButton: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 100,
    height: 38,
    width: Dimensions.get("screen").width / 2 - 24,
    justifyContent: "center",
    borderColor: color.lightGrey,
  },
  tabButtonFocused: {
    backgroundColor: color.black,
  },
  tabButtonUnfocused: {
    backgroundColor: color.white,
  },
  tabButtonText: {
    fontWeight: "500",
    fontSize: 16,
  },
  tabButtonTextFocused: {
    color: color.white,
  },
  tabButtonTextUnfocused: {
    color: color.black,
  },
  sceneContainer: {
    paddingHorizontal: 0,
    backgroundColor: color.white,
  },
  prepaidText: {
    fontSize: 10,
    color: color.blue,
    marginRight: 4,
    fontWeight: "400",
  },
});

export default BottomSheet;
